import { loadingContext } from '../core/guards/loading.context';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, Subject, throwError, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';
import { DashboardData, Rent } from '../shared/interfaces/my-properties.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public STORAGE_NAME = 'user';
  apiUrl = environment.apiUrl;
  public userDocumentation: Subject<any> = new Subject<any>();

  constructor(private http: HttpClient) {}

  /**
   * Mapea una renta del backend al formato del frontend con fechas formateadas
   */
  private mapUserRentWithFormattedDate(backendRent: any): Rent {
    const dueDateRaw = backendRent.dueDate || '';
    
    let formattedDate = '';
    try {
      formattedDate = moment.utc(backendRent.dueDate).format('DD/MM/YYYY');
      
      if (formattedDate === 'Invalid date') {
        formattedDate = '-';
      }
    } catch (e) {
      formattedDate = '-';
    }

    return {
      uuid: backendRent.uuid,
      assetId: backendRent.assetId,
      status: backendRent.status,
      dueDate: backendRent.dueDate,
      dueDateRaw: dueDateRaw,
      paymentDueDate: formattedDate,
      created_at: backendRent.created_at,
      updated_at: backendRent.updated_at,
      type: backendRent.type,
      total: backendRent.total,
      totalPerToken: backendRent.totalPerToken,
      multimediaId: backendRent.multimediaId,
      creationComment: backendRent.creationComment,
      multimedia: backendRent.multimedia,
      asset: {
        uuid: backendRent.asset.uuid,
        projectName: backendRent.asset.projectName,
        functionalUnit: backendRent.asset.functionalUnit,
        zone: backendRent.asset.zone
      }
    };
  }

  getUserDocumentation(uuid: string, showLoading = true) {
    return this.http.get<any>(`${this.apiUrl}/user-documentation/${uuid}`, { context: loadingContext(showLoading) });
  }

  getUserProfile(uuid: string, showLoading = true) {
    return this.http.get<any>(`${this.apiUrl}/users/profile/${uuid}`, { context: loadingContext(showLoading) });
  }

  updateProfile(uuid: string, body: any) {
    return this.http
      .put<any>(`${this.apiUrl}/users/update-user/${uuid}`, body)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(
            () =>
              new Error(error.error.message || 'An unexpected error occurred'),
          );
        }),
      );
  }

  getUserLevel(uuid: string) {
    return this.http.get<any>(`${this.apiUrl}/user-level/user/${uuid}`);
  }

  changePassword(request: any) {
    return this.http.post<any>(`${this.apiUrl}/auth/change-password`, request);
  }

  /**
   * Obtiene los assets del usuario con manejo correcto de fechas en las rentas
   */
  getUserAssets(userId: string) {
    return this.http.get<any>(`${this.apiUrl}/users/assets/${userId}`)
      .pipe(
        map(response => {
          // Si la respuesta tiene rentas, las procesamos
          if (response && response.rents && Array.isArray(response.rents)) {
            const processedRents = response.rents.map((rent:any) => 
              this.mapUserRentWithFormattedDate(rent)
            );
            
            return {
              ...response,
              rents: processedRents
            } as DashboardData;
          }
          
          // Si no hay rentas o la estructura es diferente, devolvemos tal como vino
          return response;
        }),
        catchError(error => {
          console.error('Error al obtener assets del usuario:', error);
          return throwError(() => error);
        })
      );
  }

  //BACKOFFICE
  getAllUsers(search?: string, pagination?: { page: number; take: number; sortBy?: string; sortDirection?: string }) {
    const query = new URLSearchParams();
    if (search) query.set('search', search);
    if (pagination) { query.set('page', String(pagination.page)); query.set('take', String(pagination.take)); }
    if (pagination?.sortBy) query.set('sortBy', pagination.sortBy);
    if (pagination?.sortDirection) query.set('sortDirection', pagination.sortDirection);
    const params = query.size ? `?${query}` : '';
    return this.http.get(`${this.apiUrl}/backoffice/users${params}`);
  }

  getUserBackofficeById(uuid: string) {
    return this.http.get(`${this.apiUrl}/backoffice/users/${uuid}`);
  }

  updateUserLevel(body: any) {
    return this.http.put(`${this.apiUrl}/user-kyc/update-user-level`, body);
  }

  blockUser(body: any) {
    return this.http.post(`${this.apiUrl}/backoffice/users/block`, body);
  }
}